import React, { useState, useMemo, useEffect, useRef } from "react";
import { API_BASE } from "../api/config";
import Layout from "../components/Layout";
import { useTable, useGlobalFilter, useSortBy, usePagination } from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../context/AuthContext";

function Tables() {
  const [data, setData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, type: "add", row: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });
  const [qrModal, setQrModal] = useState({ open: false, row: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loginType: rawLoginType, branch } = useAuth();
  const loginType = Number(rawLoginType || 0);

  const inputRef = useRef(null);
  const qrRef = useRef(null);

  useEffect(() => {
    if (modal.open && inputRef.current) inputRef.current.focus();
  }, [modal.open]);

  const fetchBranches = async () => {
    try {
      const res = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=branches&condition=status`
      );
      const json = await res.json();
      setBranches(json);
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };

  const fetchTables = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=tables`
      );
      const result = await res.json();
      const formatted = result.map((item, index) => ({
        sno: item.id,
        srno: index + 1,
        name: item.table_name,
        additional_charge_per: item.additional_charge_per,
        branch_id: item.branch_id,
        branch_name: item.branch || "",
        status: item.active_status === "1" ? "Active" : "Inactive",
      }));
      setData(formatted);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching tables:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchTables();
  }, []);

  const columns = useMemo(
    () => [
      { Header: "S.No", accessor: "srno" },
      { Header: "Table Name", accessor: "name" },
      { Header: "Additional Charge(%)", accessor: "additional_charge_per" },
      { Header: "Branch", accessor: "branch_name" },
      { Header: "Status", accessor: "status" },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
              onClick={() => setModal({ open: true, type: "edit", row: row.original })}
            >
              Edit
            </button>
			{(loginType === 1 || loginType === 2) && (
            <button
              className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              onClick={() => setDeleteModal({ open: true, row: row.original })}
            >
              Delete
            </button>
			)}
            <button
              className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => setQrModal({ open: true, row: row.original })}
            >
              Show QR
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const tableInstance = useTable(
    { columns, data, initialState: { pageSize: 5 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    setGlobalFilter,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    state: { pageIndex },
  } = tableInstance;

  const { globalFilter } = state;

  const handleSubmit = async (e) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    e.preventDefault();

    const form = e.target;
    const table_name = form.name.value;
    const active_status = form.status.value;
    const additional_charge_per = form.additional_charge_per.value;
    const branch = form.branch.value;

    const payload = { table_name, active_status, branch,additional_charge_per };

    let transac = "insert";
    if (modal.type === "edit") {
      payload.id = modal.row.sno;
      transac = "update";
    }

    try {
      const res = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=tables&transac=${transac}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();
      if (result.status === (transac === "insert" ? "inserted" : "updated")) {
        fetchTables();
        setModal({ open: false, type: "add", row: null });
      } else {
        alert("Error: " + result.message);
      }
      setIsSubmitting(false);
    } catch (err) {
      console.error("Error submitting table:", err);
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const payload = {
      key: "dzital_key",
      pswd: "Hibujji@123",
      table: "tables",
      id: deleteModal.row.sno,
      transac: "delete",
    };

    try {
      const res = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=tables&transac=delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();
      if (result.status === "deleted") {
        fetchTables();
        setDeleteModal({ open: false, row: null });
      } else {
        alert("Error deleting table: " + result.message);
      }
    } catch (err) {
      console.error("Error deleting table:", err);
    }
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tables");
    XLSX.writeFile(wb, "tables.xlsx");
  };
  const exportCSV = () => {
    const csv = [
      columns.map((c) => c.Header).join(","),
      ...data.map((r) => columns.map((c) => r[c.accessor] || "").join(",")),
    ].join("\n");
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "tables.csv");
  };
  const exportJSON = () =>
    saveAs(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }), "tables.json");

  
  const exportPDF = () => {
  const doc = new jsPDF();
  doc.text("Tables Table", 20, 10);

  autoTable(doc, {
    head: [columns.filter((c) => c.accessor).map((c) => c.Header)],
    body: data.map((row) => columns.filter((c) => c.accessor).map((c) => row[c.accessor])),
    startY: 20,
  });

  doc.save("tables.pdf");
};

  // ✅ QR helpers updated with branch name
  const downloadQR = () => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector("svg");
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => saveAs(b, `${qrModal.row.name}_QR.png`));
    };
    img.src = url;
  };

  const copyLink = () => {
    const url = `${window.location.origin}/order-self/${qrModal.row.branch_name}/${qrModal.row.name}`;
    navigator.clipboard.writeText(url).then(() => alert("Link copied to clipboard!"));
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center text-white bg-gradient-to-b from-blue-900 to-black">
          <p>Loading tables...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-black p-6 text-white">
        <h1 className="text-2xl font-bold mb-4">Tables</h1>

        {/* Controls */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="Search..."
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="p-2 border border-gray-300 rounded text-black"
            />
            <button onClick={exportExcel} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">Excel</button>
            <button onClick={exportCSV} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">CSV</button>
            <button onClick={exportPDF} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">PDF</button>
          </div>

          <div>
            <button
              onClick={() => setModal({ open: true, type: "add", row: null })}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Add Table
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white bg-opacity-10 rounded">
          <table {...getTableProps()} className="min-w-full border border-gray-300">
            <thead className="bg-gray-200 bg-opacity-20 text-white">
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      className="px-4 py-2 border cursor-pointer"
                    >
                      {col.render("Header")}
                      <span>{col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="hover:bg-gray-100 hover:bg-opacity-20">
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="px-4 py-2 border">
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-2 flex items-center gap-2">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-2 py-1 bg-gray-300 rounded">{"<<"}</button>
          <button onClick={previousPage} disabled={!canPreviousPage} className="px-2 py-1 bg-gray-300 rounded">Previous</button>
          <span>Page {pageIndex + 1} of {pageOptions.length}</span>
          <button onClick={nextPage} disabled={!canNextPage} className="px-2 py-1 bg-gray-300 rounded">Next</button>
          <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage} className="px-2 py-1 bg-gray-300 rounded">{">>"}</button>
        </div>

        {/* Add/Edit Modal */}
        {modal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-white bg-opacity-95 p-6 rounded shadow-md w-96 text-black">
              <h2 className="text-xl font-bold mb-4">
                {modal.type === "add" ? "Add Table" : "Edit Table"}
              </h2>
              <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                <input
                  type="text"
                  name="name"
                  placeholder="Table Name"
                  defaultValue={modal.row?.name || ""}
                  required
                  className="p-2 border rounded"
                  ref={inputRef}
                />
				<input
  type="text"
  name="additional_charge_per"
  placeholder="Additional Charge(%) on sale"
  defaultValue={modal.row?.additional_charge_per || ""}
  required
  className="p-2 border rounded"
  onFocus={(e) => e.target.select()}
/>
                <select
                  name="branch"
                  defaultValue={modal.row?.branch_name || ""}
                  className="p-2 border rounded"
                  required
                >
                  <option value="">Select Branch</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.name}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <select
                  name="status"
                  defaultValue={modal.row?.status === "Active" ? "1" : "0"}
                  className="p-2 border rounded"
                  required
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setModal({ open: false, type: "add", row: null })}
                    className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 text-white rounded ${
                      isSubmitting
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {deleteModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-white bg-opacity-95 p-6 rounded shadow-md w-96 text-black">
              <h2 className="text-xl font-bold mb-4 text-red-600">Confirm Delete</h2>
              <p>
                Are you sure you want to delete <strong>{deleteModal.row.name}</strong>?
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  onClick={() => setDeleteModal({ open: false, row: null })}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QR Modal with branch name in link */}
        {qrModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-md w-96 text-black text-center" ref={qrRef}>
              <h2 className="text-xl font-bold mb-4">
                QR Code for {qrModal.row.branch_name} / {qrModal.row.name}
              </h2>
              {qrModal.row && (
                <>
                  <QRCodeSVG
                    value={`thehomeportnoiv.com/order-self/${qrModal.row.branch_name}/${qrModal.row.name}`}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                  <a
                    href={`thehomeportnoiv.com/order-self/${qrModal.row.branch_name}/${qrModal.row.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-3 text-blue-600 hover:underline break-all"
                  >
                    {`thehomeportnoiv.com/order-self/${qrModal.row.branch_name}/${qrModal.row.name}`}
                  </a>
                  <button
                    className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    onClick={copyLink}
                  >
                    Copy Link
                  </button>
                </>
              )}
              <p className="mt-2 text-sm text-gray-600">
                Scan or copy the link to open orders page
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  onClick={downloadQR}
                >
                  Download PNG
                </button>
                <button
                  className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  onClick={() => setQrModal({ open: false, row: null })}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Tables;
